import type {
    Form,
    FormButton,
    FormField,
    FormFieldConditions,
    FormSection,
    FormUploadButton,
    ReactiveDropdownCatalogs,
    ReactiveFieldCondition,
    ReactiveFieldConditionRule,
    ReactiveFieldLogicContext,
} from '../../interfaces/form.interface';
import { FormType } from '../../interfaces/form.interface';
import type { Input } from '../../interfaces/input.interface';
import {
    ReactiveFieldDefinition,
    ReactiveDropdownDefinition,
    ReactiveFormDefinition,
    ReactiveFormMode,
    ReactiveRule,
    ReactiveRuleConfiguration,
    ReactiveSectionDefinition,
} from '../../interfaces/reactive-form-definition.interface';
import type { ReactiveFieldDependency, ReactiveDependencyKind } from '../dependencies/reactive-dependency-graph';

const DEFAULT_UPLOAD_BUTTON: FormUploadButton = {
    isEnabled: false,
    label: '',
};

const DEFAULT_FIELD_CONDITIONS: FormFieldConditions = {
    isRememberedOnDuplicate: false,
    isRequired: false,
    isRetained: false,
    hasUndoEnabled: false,
    isVisible: true,
};

const FORM_TYPE_TO_MODE = new Map<FormType, ReactiveFormMode>([
    [FormType.CREATION, 'creation'],
    [FormType.EDITION, 'edition'],
    [FormType.DUPLICATION, 'duplication'],
    [FormType.DELETION, 'deletion'],
    [FormType.INFORMATIVE, 'informative'],
    [FormType.EXPORT, 'export'],
    [FormType.GENERATION, 'generation'],
]);

const MODE_TO_FORM_TYPE = new Map<ReactiveFormMode, FormType>([
    ['creation', FormType.CREATION],
    ['edition', FormType.EDITION],
    ['duplication', FormType.DUPLICATION],
    ['deletion', FormType.DELETION],
    ['informative', FormType.INFORMATIVE],
    ['export', FormType.EXPORT],
    ['generation', FormType.GENERATION],
]);

/**
 * Bridges the legacy `Form<T>` schema and the typed `ReactiveFormDefinition<T>` schema.
 *
 * The adapter is intentionally lossless for legacy definitions: callbacks, render settings,
 * request hooks and other unknown properties are kept in `legacy` metadata and restored when
 * the definition is converted back to the runtime `Form<T>` shape.
 */
export class LegacyFormDefinitionAdapter {
    public static fromLegacy<TModel>(form: Form<TModel>): ReactiveFormDefinition<TModel> {
        return {
            name: form.name,
            title: form.header,
            mode: this.toMode(form.type),
            sections: form.sections.map((section) => this.fromLegacySection(section)),
            table: form.table,
            uploadButton: form.uploadButton,
            ...(form.uploadButtons ? { uploadButtons: [...form.uploadButtons] } : {}),
            ...(form.button ? { button: this.cloneButtonOrButtons(form.button) } : {}),
            ...(form.buttons ? { buttons: [...form.buttons] } : {}),
            signed: form.signed,
            ...(form.signatureConfiguration ? { signatureConfiguration: form.signatureConfiguration } : {}),
            ...(form.serializer ? { serializer: form.serializer } : {}),
            ...(form.dependencies ? { dependencies: [...form.dependencies] } : {}),
            ...(form.dropdownCatalogs ? { dropdownCatalogs: this.cloneDropdownCatalogs(form.dropdownCatalogs) } : {}),
            legacy: {
                source: 'legacy-form',
                form,
                ...(form.previousDataTransform ? { previousDataTransform: form.previousDataTransform } : {}),
                ...(form.beforeRepeatableHydration ? { beforeRepeatableHydration: form.beforeRepeatableHydration } : {}),
            },
        };
    }

    public static toLegacy<TModel>(definition: ReactiveFormDefinition<TModel>): Form<TModel> {
        const sourceForm = definition.legacy?.form as Form<TModel> | undefined;
        const uploadButton = definition.uploadButton ?? sourceForm?.uploadButton ?? DEFAULT_UPLOAD_BUTTON;
        const signed = definition.signed ?? sourceForm?.signed ?? false;
        const button = this.resolveButton(definition.button, sourceForm?.button);
        const buttons = this.resolveReadonlyArray(definition.buttons, sourceForm?.buttons);
        const uploadButtons = this.resolveReadonlyArray(definition.uploadButtons, sourceForm?.uploadButtons);

        return {
            ...(sourceForm ?? {}),
            name: definition.name,
            header: definition.title,
            type: this.toFormType(definition.mode),
            sections: definition.sections.map((section) => this.toLegacySection(section)),
            table: definition.table ?? sourceForm?.table ?? null,
            uploadButton,
            ...(uploadButtons ? { uploadButtons } : {}),
            ...(button ? { button } : {}),
            ...(buttons ? { buttons } : {}),
            signed,
            ...(definition.signatureConfiguration ? { signatureConfiguration: definition.signatureConfiguration } : {}),
            ...(definition.serializer ? { serializer: definition.serializer } : {}),
            ...(sourceForm?.previousDataTransform ? { previousDataTransform: sourceForm.previousDataTransform } : {}),
            ...(sourceForm?.beforeRepeatableHydration ? { beforeRepeatableHydration: sourceForm.beforeRepeatableHydration } : {}),
            ...(definition.dependencies ? { dependencies: [...definition.dependencies] } : {}),
            ...this.resolveDropdownCatalogs(definition.dropdownCatalogs, sourceForm?.dropdownCatalogs),
        };
    }

    public static toMode(type: FormType): ReactiveFormMode {
        return FORM_TYPE_TO_MODE.get(type) ?? 'creation';
    }

    public static toFormType(mode: ReactiveFormMode): FormType {
        return MODE_TO_FORM_TYPE.get(mode) ?? FormType.CREATION;
    }

    private static fromLegacySection<TModel>(section: FormSection<TModel>): ReactiveSectionDefinition<TModel> {
        return {
            name: section.name,
            width: section.width,
            order: section.order,
            visible: section.isVisible,
            ...(section.visibleWhen !== undefined ? { visibleWhen: section.visibleWhen } : {}),
            fields: section.fields.map((field) => this.fromLegacyField(field)),
            ...(section.buttons ? { buttons: [...section.buttons] } : {}),
            ...(section.tables ? { tables: [...section.tables] } : {}),
            legacy: {
                section,
                ...(section.visibleWhen !== undefined ? { visibleWhen: section.visibleWhen } : {}),
            },
        };
    }

    private static fromLegacyField<TModel>(field: FormField<TModel>): ReactiveFieldDefinition<TModel> {
        const conditions = field.conditions ?? DEFAULT_FIELD_CONDITIONS;

        return {
            name: field.name as keyof TModel & string,
            label: field.label,
            type: field.input.type,
            width: field.width,
            order: field.order,
            input: field.input,
            visible: conditions.isVisible,
            required: conditions.isRequired,
            readOnly: !field.input.isEditable,
            ...(field.computed ? { computed: field.computed } : {}),
            ...(field.labelWhen ? { labelWhen: field.labelWhen } : {}),
            ...(field.input.defaultValue !== undefined ? { defaultValue: field.input.defaultValue } : {}),
            ...(field.dataSource || field.dependencyBehaviour
                ? { dropdown: this.buildDropdownDefinition(field) }
                : {}),
            ...this.withDependencies(this.buildFieldDependencies(field)),
            ...(field.buttons ? { buttons: [...field.buttons] } : {}),
            legacy: {
                field,
                conditions,
                ...(field.turnVisible ? { turnVisible: field.turnVisible } : {}),
                ...(field.updateValue ? { updateValue: field.updateValue } : {}),
                ...(field.checkForDuplicates ? { checkForDuplicates: field.checkForDuplicates } : {}),
            },
        };
    }

    private static toLegacySection<TModel>(section: ReactiveSectionDefinition<TModel>): FormSection<TModel> {
        const sourceSection = section.legacy?.section as FormSection<TModel> | undefined;
        const visibleWhen = section.visibleWhen ?? sourceSection?.visibleWhen;

        return {
            ...(sourceSection ?? {}),
            name: section.name,
            fields: section.fields.map((field) => this.toLegacyField(field)),
            ...(section.buttons ? { buttons: [...section.buttons] } : {}),
            ...(section.tables ? { tables: [...section.tables] } : {}),
            width: section.width,
            isVisible: section.visible,
            ...(visibleWhen !== undefined ? { visibleWhen } : {}),
            order: section.order,
        };
    }

    private static toLegacyField<TModel>(field: ReactiveFieldDefinition<TModel>): FormField<TModel> {
        const sourceField = field.legacy?.field as FormField<TModel> | undefined;
        const conditions = this.buildLegacyConditions(field, sourceField?.conditions);
        const input = this.buildLegacyInput(field, sourceField?.input);
        const dependencies = this.buildRuntimeDependencies(field, sourceField?.dependencies);
        const buttons = this.resolveReadonlyArray(field.buttons, sourceField?.buttons);
        const computed = field.computed ?? sourceField?.computed;
        const labelWhen = field.labelWhen ?? sourceField?.labelWhen;
        const runtimeRules = this.buildRuntimeRules(field, sourceField);

        return {
            ...(sourceField ?? {}),
            name: field.name,
            label: field.label,
            input,
            ...runtimeRules,
            ...(field.dropdown?.dataSource ? { dataSource: field.dropdown.dataSource } : {}),
            ...(field.dropdown?.dependencyBehaviour ? { dependencyBehaviour: field.dropdown.dependencyBehaviour } : {}),
            ...(computed ? { computed } : {}),
            ...(labelWhen ? { labelWhen } : {}),
            ...(dependencies.length > 0 ? { dependencies } : {}),
            ...(buttons ? { buttons } : {}),
            width: field.width,
            conditions,
            order: field.order,
        };
    }


    private static buildFieldDependencies<TModel>(field: FormField<TModel>): ReactiveFieldDependency[] {
        const dependencies: ReactiveFieldDependency[] = [];

        for (const dependency of field.dependencies ?? []) {
            this.addFieldDependency(dependencies, dependency);
        }

        for (const source of field.dataSource?.dependsOn ?? []) {
            this.addFieldDependency(dependencies, {
                source,
                target: field.name,
                kind: 'dropdown',
                clearOnChange: field.dependencyBehaviour?.clearOnParentChange ?? true,
                reloadOnChange: field.dependencyBehaviour?.reloadOnParentChange ?? false,
                filterLocally: field.dependencyBehaviour?.filterLocally ?? false,
                preserveOnHydration: field.dependencyBehaviour?.clearOnHydration === false,
                legacy: true,
            });
        }

        for (const source of this.toDependencyArray(field.computed?.dependsOn)) {
            this.addFieldDependency(dependencies, {
                source,
                target: field.name,
                kind: 'computed',
            });
        }

        this.addRuleDependencies(dependencies, field, field.visibleWhen, 'visibility');
        this.addRuleDependencies(dependencies, field, field.readonlyWhen, 'readonly');
        this.addRuleDependencies(dependencies, field, field.disabledWhen, 'disabled');

        for (const source of this.toDependencyArray(field.input?.dynamicTypeConfiguration?.dependsOn)) {
            this.addFieldDependency(dependencies, {
                source,
                target: field.name,
                kind: 'dynamicType',
                legacy: true,
            });
        }

        return dependencies;
    }

    private static addRuleDependencies<TModel>(
        dependencies: ReactiveFieldDependency[],
        field: FormField<TModel>,
        rule: ReactiveFieldConditionRule<TModel> | undefined,
        kind: ReactiveDependencyKind,
    ): void {
        if (!this.isRuntimeRuleObject(rule)) {
            return;
        }

        for (const source of rule.dependsOn ?? []) {
            this.addFieldDependency(dependencies, {
                source,
                target: field.name,
                kind,
            });
        }
    }

    private static buildRuntimeDependencies<TModel>(
        field: ReactiveFieldDefinition<TModel>,
        sourceDependencies: readonly ReactiveFieldDependency[] | undefined,
    ): ReactiveFieldDependency[] {
        const dependencies: ReactiveFieldDependency[] = [];

        for (const dependency of sourceDependencies ?? []) {
            this.addFieldDependency(dependencies, dependency);
        }

        for (const dependency of field.dependencies ?? []) {
            this.addFieldDependency(dependencies, dependency);
        }

        this.addDefinitionRuleDependencies(dependencies, field.name, field.visibleWhen ?? field.visible, 'visibility');
        this.addDefinitionRuleDependencies(dependencies, field.name, field.readonlyWhen ?? field.readOnly, 'readonly');
        this.addDefinitionRuleDependencies(dependencies, field.name, field.disabledWhen, 'disabled');

        return dependencies;
    }

    private static addDefinitionRuleDependencies<TModel>(
        dependencies: ReactiveFieldDependency[],
        target: string,
        rule: ReactiveRule<TModel> | undefined,
        kind: ReactiveDependencyKind,
    ): void {
        if (!this.isDefinitionRuleObject(rule)) {
            return;
        }

        for (const source of rule.dependsOn ?? []) {
            this.addFieldDependency(dependencies, {
                source,
                target,
                kind,
            });
        }
    }

    private static withDependencies(dependencies: ReactiveFieldDependency[]): {
        dependencies?: ReactiveFieldDependency[];
    } {
        return dependencies.length > 0 ? { dependencies } : {};
    }

    private static toDependencyArray(dependencies: string | readonly string[] | undefined): readonly string[] {
        if (!dependencies) {
            return [];
        }

        return typeof dependencies === 'string' ? [dependencies] : dependencies;
    }

    private static addFieldDependency(dependencies: ReactiveFieldDependency[], dependency: ReactiveFieldDependency): void {
        if (!dependency.source || !dependency.target) {
            return;
        }

        const exists = dependencies.some((item) =>
            item.source === dependency.source &&
            item.target === dependency.target &&
            item.kind === dependency.kind,
        );

        if (!exists) {
            dependencies.push(dependency);
        }
    }

    private static buildDropdownDefinition<TModel>(field: FormField<TModel>): ReactiveDropdownDefinition<TModel> {
        return {
            ...(field.dataSource ? { dataSource: field.dataSource } : {}),
            ...(field.dependencyBehaviour ? { dependencyBehaviour: field.dependencyBehaviour } : {}),
        };
    }

    private static buildLegacyConditions<TModel>(
        field: ReactiveFieldDefinition<TModel>,
        sourceConditions: FormFieldConditions | undefined,
    ): FormFieldConditions {
        return {
            ...(sourceConditions ?? DEFAULT_FIELD_CONDITIONS),
            isRequired: this.resolveStaticRule(field.required, sourceConditions?.isRequired ?? false),
            isVisible: this.resolveStaticRule(field.visibleWhen ?? field.visible, sourceConditions?.isVisible ?? true),
        };
    }

    private static buildRuntimeRules<TModel>(
        field: ReactiveFieldDefinition<TModel>,
        sourceField: FormField<TModel> | undefined,
    ): Partial<FormField<TModel>> {
        const visibleRule = field.visibleWhen ?? (sourceField?.visibleWhen === undefined ? field.visible : undefined);
        const readonlyRule = field.readonlyWhen ?? (sourceField?.readonlyWhen === undefined ? field.readOnly : undefined);
        const disabledRule = field.disabledWhen;

        return {
            ...(visibleRule !== undefined ? { visibleWhen: this.toRuntimeRule(field.name, visibleRule) } : {}),
            ...(readonlyRule !== undefined ? { readonlyWhen: this.toRuntimeRule(field.name, readonlyRule) } : {}),
            ...(disabledRule !== undefined ? { disabledWhen: this.toRuntimeRule(field.name, disabledRule) } : {}),
        };
    }

    private static toRuntimeRule<TModel>(
        fieldName: keyof TModel & string,
        rule: ReactiveRule<TModel>,
    ): ReactiveFieldConditionRule<TModel> {
        if (typeof rule === 'boolean') {
            return rule;
        }

        if (typeof rule === 'function') {
            return (context: ReactiveFieldLogicContext<TModel>): boolean =>
                rule(this.createRuleContext(fieldName, context));
        }

        const resolve = (context: ReactiveFieldLogicContext<TModel>): boolean =>
            rule.resolve(this.createRuleContext(fieldName, context));

        return rule.dependsOn ? { dependsOn: rule.dependsOn, resolve } : { resolve };
    }

    private static createRuleContext<TModel>(
        fieldName: keyof TModel & string,
        context: ReactiveFieldLogicContext<TModel>,
    ): {
        model: Partial<TModel>;
        previousData: TModel | null;
        fieldName: keyof TModel & string;
    } {
        return {
            model: context.getRawValue() as Partial<TModel>,
            previousData: null,
            fieldName,
        };
    }

    private static buildLegacyInput<TModel>(
        field: ReactiveFieldDefinition<TModel>,
        sourceInput: Input<TModel> | undefined,
    ): Input<TModel> {
        const readOnly = this.resolveStaticRule(
            field.disabledWhen ?? field.readonlyWhen ?? field.readOnly,
            sourceInput ? !sourceInput.isEditable : false,
        );

        return {
            ...(sourceInput ?? field.input),
            ...field.input,
            type: field.type,
            isEditable: !readOnly,
            ...(field.defaultValue !== undefined ? { defaultValue: field.defaultValue } : {}),
        } as Input<TModel>;
    }

    private static isRuntimeRuleObject<TModel>(
        rule: ReactiveFieldConditionRule<TModel> | undefined,
    ): rule is ReactiveFieldCondition<TModel> {
        return typeof rule === 'object' && rule !== null && typeof rule.resolve === 'function';
    }

    private static isDefinitionRuleObject<TModel>(
        rule: ReactiveRule<TModel> | undefined,
    ): rule is ReactiveRuleConfiguration<TModel> {
        return typeof rule === 'object' && rule !== null && typeof rule.resolve === 'function';
    }

    private static resolveStaticRule<TModel>(rule: ReactiveRule<TModel> | undefined, fallback: boolean): boolean {
        if (typeof rule === 'boolean') {
            return rule;
        }

        return fallback;
    }

    private static resolveDropdownCatalogs(
        nextCatalogs: ReactiveDropdownCatalogs | undefined,
        fallbackCatalogs: ReactiveDropdownCatalogs | undefined,
    ): { dropdownCatalogs?: ReactiveDropdownCatalogs } {
        const catalogs = nextCatalogs ?? fallbackCatalogs;

        return catalogs ? { dropdownCatalogs: this.cloneDropdownCatalogs(catalogs) } : {};
    }

    private static cloneDropdownCatalogs(catalogs: ReactiveDropdownCatalogs): ReactiveDropdownCatalogs {
        return {
            ...(catalogs.critical ? { critical: [...catalogs.critical] } : {}),
            ...(catalogs.deferred ? { deferred: [...catalogs.deferred] } : {}),
            ...(catalogs.lazy ? { lazy: [...catalogs.lazy] } : {}),
        };
    }

    private static resolveButton(
        nextButton: FormButton | readonly FormButton[] | undefined,
        fallbackButton: FormButton | readonly FormButton[] | undefined,
    ): FormButton | FormButton[] | undefined {
        if (nextButton) {
            return Array.isArray(nextButton) ? [...nextButton] : nextButton as FormButton;
        }

        if (fallbackButton) {
            return Array.isArray(fallbackButton) ? [...fallbackButton] : fallbackButton as FormButton;
        }

        return undefined;
    }

    private static resolveReadonlyArray<TValue>(
        nextValues: readonly TValue[] | undefined,
        fallbackValues: readonly TValue[] | undefined,
    ): TValue[] | undefined {
        if (nextValues) {
            return [...nextValues];
        }

        return fallbackValues ? [...fallbackValues] : undefined;
    }

    private static cloneButtonOrButtons(button: FormButton | FormButton[]): FormButton | FormButton[] {
        return Array.isArray(button) ? [...button] : button;
    }
}
